import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import './EventBulkInvoices.css'
import { connect } from 'react-redux'
import { Button, Table, Row, Col, Collapse, Form } from 'react-bootstrap'
import DataTable from '../_library/DataTable'
import Card from '../_library/Card'
import InfoTooltip from '../_library/InfoTooltip'
import ActionsSelect from '../_library/ActionsSelect'
import BulkInvoiceModals from './components/BulkInvoiceModals'
import { 
  FETCH_EVENT_BULK_INVOICES, 
  VOID_BULK_INVOICE, 
  SEND_BULK_INVOICE, 
  REFRESH_BULK_INVOICE,
  CREATE_DELEGATION 
} from '../../_common/redux/bulkInvoices/actions'
import { formatCurrency } from '../utils/currencyUtils'
import { formatDate } from '../../_common/core/validation/normalizers'
import BulkInvoiceForm from './BulkInvoiceForm'
import BulkInvoiceDetails from './BulkInvoiceDetails'

const EventBulkInvoices = props => {
  const { event, bulkInvoices, isLoading, match, history } = props

  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [voidInvoice, setVoidInvoice] = useState(null)
  const [voidReason, setVoidReason] = useState('')
  const [isVoiding, setIsVoiding] = useState(false)
  const [showSendModal, setShowSendModal] = useState(false)
  const [sendInvoice, setSendInvoice] = useState(null)
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    const eventIdFromUrl = match?.params?.id
    
    if (event?.id) {
      props.fetchBulkInvoices(event.id)
    } else if (eventIdFromUrl) {
      props.fetchBulkInvoices(eventIdFromUrl)
    }
  }, [event?.id, match?.params?.id])

  const handleCreateInvoice = () => {
    const eventId = event?.id || match?.params?.id
    history.push(`/event/${eventId}/bulk-invoices/new`)
  }

  const handleViewDetails = invoice => {
    // Get event ID from props or URL params as fallback
    const eventId = event?.id || match?.params?.id
    const invoiceId = invoice?.id
    
    if (!eventId) {
      console.error('Event ID not found')
      return
    }
    
    if (!invoiceId) {
      console.error('Invoice ID not found')
      return
    }
    
    // Navigate to the dedicated invoice page instead of opening modal
    history.push(`/event/${eventId}/bulk-invoices/${invoiceId}`)
  }

  const handleEditInvoice = invoice => {
    // Get event ID from props or URL params as fallback
    const eventId = event?.id || match?.params?.id
    const invoiceId = invoice?.id
    
    if (!eventId) {
      console.error('Event ID not found')
      return
    }
    
    if (!invoiceId) {
      console.error('Invoice ID not found')
      return
    }
    
    // Navigate to the edit invoice page
    history.push(`/event/${eventId}/bulk-invoices/${invoiceId}/edit`)
  }

  const handleSendInvoice = invoice => {
    setSendInvoice(invoice)
    setShowSendModal(true)
  }

  const confirmSend = async () => {
    if (sendInvoice) {
      setIsSending(true)
      try {
        const eventId = event?.id || match?.params?.id
        await props.sendBulkInvoice(eventId, sendInvoice.id)
        setShowSendModal(false)
        setSendInvoice(null)
      } finally {
        setIsSending(false)
      }
    }
  }

  const handleRefreshInvoice = async invoice => {
    try {
      const eventId = event?.id || match?.params?.id
      await props.refreshBulkInvoice(eventId, invoice.id)
      // Invoice list will be refreshed automatically by the action
    } catch (error) {
      // Error already handled by Redux action with showAxiosError
    }
  }

  const handleCreateDelegation = async invoice => {
    try {
      const eventId = event?.id || match?.params?.id
      await props.createDelegation(eventId, invoice.id)
      // Invoice list will be refreshed automatically by the action
    } catch (error) {
      // Error already handled by Redux action with showAxiosError
    }
  }

  // Action condition functions with defensive programming - match BulkInvoiceViewPage rules
  // Simple flag-based conditions - let backend handle all business logic
  const canViewDetails = (row) => {
    return true // View details should always be available
  }

  const canEdit = (row) => {
    return Boolean(row?.can_edit)
  }

  const canSend = (row) => {
    return Boolean(row?.can_send)
  }

  const canRefresh = (row) => {
    return Boolean(row?.can_refresh)
  }

  const canViewDelegation = (row) => {
    return Boolean(row?.status === 'paid' && row?.delegation_id)
  }

  const canCreateDelegation = (row) => {
    return Boolean(row?.can_create_delegation)
  }

  const canVoid = (row) => {
    return Boolean(row?.can_void)
  }

  const canShowDivider = (row) => {
    return Boolean(row?.can_void) // Show divider if void action is available
  }

  const handleVoidInvoice = invoice => {
    setVoidInvoice(invoice)
    setVoidReason('')
    setShowVoidModal(true)
  }

  const confirmVoidInvoice = async () => {
    if (voidInvoice) {
      setIsVoiding(true)
      try {
        const eventId = event?.id || match?.params?.id || voidInvoice?.event_id
        await props.voidBulkInvoice(eventId, voidInvoice.id, voidReason)
        setShowVoidModal(false)
        setVoidInvoice(null)
        setVoidReason('')
      } finally {
        setIsVoiding(false)
      }
    }
  }

  const getStatusBadge = status => {
    const statusColors = {
      draft: 'warning',
      open: 'info',
      paid: 'success',
      void: 'danger',
      uncollectible: 'danger'
    }
    return <span className={`badge badge-${statusColors[status] || 'secondary'}`}>{status}</span>
  }

  const columns = [
    {
      key: 'invoice_number',
      label: 'Invoice Number',
      sortable: true,
      render: row => (
        <a href="#" onClick={e => {
          e.preventDefault()
          handleViewDetails(row)
        }}>
          {row.invoice_number}
        </a>
      )
    },
    {
      key: 'customer_name',
      label: 'Customer',
      sortable: true,
      render: row => (
        <div>
          <div>{row.customer_name}</div>
          {row.customer_company && <small className="text-muted">{row.customer_company}</small>}
        </div>
      )
    },
    {
      key: 'total_amount',
      label: 'Total',
      sortable: true,
      render: row => formatCurrency(row.total_amount, row.currency)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: row => getStatusBadge(row.status)
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: row => formatDate(row.created_at)
    },
    {
      key: 'paid_at',
      label: 'Paid',
      sortable: true,
      render: row => row.paid_at ? formatDate(row.paid_at) : '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => {
        const actions = [
          {
            key: 'view',
            label: 'View Details',
            icon: 'fa-eye',
            onClick: () => handleViewDetails(row),
            condition: true
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: 'fa-edit',
            onClick: () => handleEditInvoice(row),
            condition: canEdit(row)
          },
          {
            key: 'send',
            label: 'Send',
            icon: 'fa-paper-plane',
            onClick: () => {
              // Send invoice directly without opening edit modal
              handleSendInvoice(row)
            },
            condition: canSend(row)
          },
          {
            key: 'refresh',
            label: 'Refresh from Stripe',
            icon: 'fa-sync',
            onClick: () => handleRefreshInvoice(row),
            condition: canRefresh(row)
          },
          {
            key: 'delegation',
            label: 'View Delegation',
            icon: 'fa-users',
            onClick: () => history.push(`/event/${event?.id || match?.params?.id}/delegations`),
            condition: canViewDelegation(row)
          },
          {
            key: 'create-delegation',
            label: 'Create Delegation',
            icon: 'fa-user-plus',
            onClick: () => handleCreateDelegation(row),
            condition: canCreateDelegation(row)
          },
          {
            key: 'divider',
            divider: true,
            condition: canShowDivider(row)
          },
          {
            key: 'void',
            label: 'Void',
            icon: 'fa-ban',
            onClick: () => handleVoidInvoice(row),
            condition: canVoid(row),
          }
        ]

        return <ActionsSelect actions={actions} row={row} />
      }
    }
  ]

  return (
    <div className="event-bulk-invoices">
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginRight: '12px' }}>
            <span>
              Bulk Invoices
              <InfoTooltip text="Invoice actions and status information" id="bulk-invoices-info" content="Create and manage invoices for bulk ticket purchases. When paid, a delegation is automatically created for ticket distribution." />
            </span>
            <Button className="btn btn-success btn-shadow" onClick={handleCreateInvoice} style={{ marginLeft: 'auto' }}>
              <i className="fa fa-plus" /> Create Invoice
            </Button>
          </div>
        }
      >
        {isLoading ? (
          <div className="text-center p-3">
            <i className="fa fa-spinner fa-spin" /> Loading invoices...
          </div>
        ) : (
          <DataTable
            thead={columns.map(col => col.label)}
            tbody={bulkInvoices.length > 0 ? bulkInvoices.map(invoice => 
              columns.map(col => col.render ? col.render(invoice) : invoice[col.key])
            ) : []}
          />
        )}
        {!isLoading && bulkInvoices.length === 0 && (
          <div className="text-center text-muted p-4">
            No bulk invoices found
          </div>
        )}
      </Card>

      {/* Custom CSS for modal and actions dropdown */}
      <style>{`
        /* Actions dropdown styling */
        .btn-actions-dropdown {
          border: 1px solid #dee2e6 !important;
          background: #fff !important;
          color: #6c757d !important;
          padding: 0.25rem 0.5rem !important;
          min-width: 32px !important;
          box-shadow: none !important;
        }
        .btn-actions-dropdown:hover,
        .btn-actions-dropdown:focus,
        .btn-actions-dropdown.focus {
          background: #f8f9fa !important;
          border-color: #adb5bd !important;
          color: #495057 !important;
          box-shadow: none !important;
        }
        .btn-actions-dropdown.active,
        .btn-actions-dropdown:active,
        .open > .btn-actions-dropdown {
          background: #e9ecef !important;
          border-color: #adb5bd !important;
          color: #495057 !important;
          box-shadow: inset 0 3px 5px rgba(0,0,0,.125) !important;
        }
        
        /* Dropdown menu styling */
        .btn-actions-dropdown + .dropdown-menu {
          background: #fff !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          border-radius: 0.25rem !important;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          min-width: 160px !important;
          margin-top: 2px !important;
          z-index: 1000 !important;
        }
        
        /* Dropdown items styling */
        .btn-actions-dropdown + .dropdown-menu > li > a {
          padding: 0.5rem 1rem !important;
          font-size: 0.875rem !important;
          color: #212529 !important;
          text-decoration: none !important;
          display: block !important;
          clear: both !important;
          font-weight: normal !important;
          line-height: 1.42857143 !important;
          white-space: nowrap !important;
        }
        
        .btn-actions-dropdown + .dropdown-menu > li > a:hover,
        .btn-actions-dropdown + .dropdown-menu > li > a:focus {
          background-color: #f8f9fa !important;
          color: #16181b !important;
        }
        
        .btn-actions-dropdown + .dropdown-menu > li.divider {
          height: 1px !important;
          margin: 0.5rem 0 !important;
          overflow: hidden !important;
          background-color: #e9ecef !important;
        }
        
        /* Danger item styling */
        .btn-actions-dropdown + .dropdown-menu > li > a.text-danger {
          color: #dc3545 !important;
        }
        
        .btn-actions-dropdown + .dropdown-menu > li > a.text-danger:hover,
        .btn-actions-dropdown + .dropdown-menu > li > a.text-danger:focus {
          background-color: #dc3545 !important;
          color: #fff !important;
        }
        
        /* Modal styling */
        .modal-backdrop {
          background-color: rgba(0, 0, 0, 0.9) !important;
          opacity: 1 !important;
          z-index: 1040 !important;
        }
        .bulk-invoice-modal {
          z-index: 1050 !important;
        }
        .bulk-invoice-modal * {
          transition: none !important;
        }
        .bulk-invoice-modal .modal-dialog {
          background-color: #1a202c !important;
          border-radius: 8px !important;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8) !important;
          z-index: 1051 !important;
          position: relative;
        }
        .bulk-invoice-modal .modal-content {
          background-color: #1a202c !important;
          background: #1a202c !important;
          border: none !important;
          color: #ffffff !important;
          opacity: 1 !important;
        }
        .bulk-invoice-modal .modal-header {
          background-color: #1a202c !important;
          background: #1a202c !important;
          border-bottom: 1px solid #4a5568 !important;
          color: #ffffff !important;
        }
        .bulk-invoice-modal .modal-body {
          background-color: #1a202c !important;
          background: #1a202c !important;
          color: #ffffff !important;
        }
        .bulk-invoice-modal .modal-title {
          color: #ffffff !important;
          font-weight: 600 !important;
        }
        .bulk-invoice-modal .btn-close {
          filter: invert(1) !important;
          opacity: 1 !important;
        }
        .bulk-invoice-modal input,
        .bulk-invoice-modal textarea,
        .bulk-invoice-modal select {
          background-color: #2d3748 !important;
          border: 1px solid #4a5568 !important;
          color: #ffffff !important;
        }
        .bulk-invoice-modal .form-label,
        .bulk-invoice-modal label,
        .bulk-invoice-modal h5,
        .bulk-invoice-modal .text-muted,
        .bulk-invoice-modal p,
        .bulk-invoice-modal span,
        .bulk-invoice-modal div {
          color: #ffffff !important;
        }
        .bulk-invoice-modal .table {
          color: #ffffff !important;
        }
        .bulk-invoice-modal .table th,
        .bulk-invoice-modal .table td {
          color: #ffffff !important;
          border-color: #4a5568 !important;
        }
        .bulk-invoice-modal .show,
        .bulk-invoice-modal.show {
          opacity: 1 !important;
        }
        .bulk-invoice-modal .show *,
        .bulk-invoice-modal.show * {
          color: #ffffff !important;
        }
        /* Ultra-specific rules to force white text */
        .modal.bulk-invoice-modal.show .modal-dialog .modal-content .modal-body *,
        .modal.bulk-invoice-modal .modal-dialog .modal-content .modal-body *,
        .bulk-invoice-modal .modal-body * {
          color: #ffffff !important;
          opacity: 1 !important;
        }
        /* Force all text elements */
        .bulk-invoice-modal label[class*=""],
        .bulk-invoice-modal span[class*=""],
        .bulk-invoice-modal div[class*=""],
        .bulk-invoice-modal p[class*=""] {
          color: #ffffff !important;
        }
        /* Override any animations */
        .bulk-invoice-modal.fade.show,
        .bulk-invoice-modal.fade.show *,
        .bulk-invoice-modal.fade.in,
        .bulk-invoice-modal.fade.in * {
          color: #ffffff !important;
          opacity: 1 !important;
        }
        
        /* Void Invoice Modal - Fix dimming while preserving theme */
        .void-invoice-modal {
          z-index: 9999 !important;
        }
        .void-invoice-modal .modal-dialog {
          z-index: 10000 !important;
          position: relative;
          opacity: 1 !important;
          filter: brightness(1.5) contrast(1.2) !important;
          transform: none !important;
        }
        .void-invoice-modal .modal-content {
          z-index: 10001 !important;
          position: relative;
          opacity: 1 !important;
          filter: brightness(1.5) contrast(1.2) !important;
        }
        .void-invoice-modal .modal-header,
        .void-invoice-modal .modal-body,
        .void-invoice-modal .modal-footer {
          opacity: 1 !important;
          filter: brightness(1.5) contrast(1.2) !important;
        }
        .void-invoice-modal * {
          opacity: 1 !important;
          filter: brightness(1.2) !important;
        }
        .send-invoice-modal,
        .send-invoice-modal *,
        .send-invoice-modal .modal-dialog,
        .send-invoice-modal .modal-content,
        .send-invoice-modal .modal-header,
        .send-invoice-modal .modal-body,
        .send-invoice-modal .modal-footer,
        .send-invoice-modal .btn,
        .send-invoice-modal .modal-title,
        .send-invoice-modal p,
        .send-invoice-modal i {
          opacity: 1 !important;
          filter: brightness(1) !important;
          background-color: initial !important;
        }
        .send-invoice-modal {
          z-index: 10050 !important;
        }
        .modal-backdrop.void-invoice-modal {
          z-index: 9998 !important;
        }
      `}</style>


      {/* Invoice Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false)
          setSelectedInvoice(null)
        }}
        size="lg"
        className="bulk-invoice-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <BulkInvoiceDetails
              event={event}
              invoice={selectedInvoice}
              onClose={() => {
                setShowDetailsModal(false)
                setSelectedInvoice(null)
              }}
              onRefresh={() => props.fetchBulkInvoices(event.id)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Bulk Invoice Modals */}
      <BulkInvoiceModals
        showVoidModal={!!voidInvoice}
        voidInvoice={voidInvoice}
        voidReason={voidReason}
        setVoidReason={setVoidReason}
        onVoidCancel={() => {
          setShowVoidModal(false)
          setVoidInvoice(null)
          setVoidReason('')
        }}
        onVoidConfirm={confirmVoidInvoice}
        isVoiding={isVoiding}
        showSendModal={showSendModal}
        sendInvoice={sendInvoice}
        onSendCancel={() => {
          setShowSendModal(false)
          setSendInvoice(null)
        }}
        onSendConfirm={confirmSend}
        isSending={isSending}
      />

    </div>
  )
}

const mapStateToProps = state => ({
  event: state.events.selected,
  bulkInvoices: state.bulkInvoices.list,
  isLoading: state.loading.has('FETCH_EVENT_BULK_INVOICES')
})

const mapDispatchToProps = dispatch => ({
  fetchBulkInvoices: eventId => dispatch(FETCH_EVENT_BULK_INVOICES(eventId)),
  voidBulkInvoice: (eventId, invoiceId, reason) => 
    dispatch(VOID_BULK_INVOICE(eventId, invoiceId, reason)),
  sendBulkInvoice: (eventId, invoiceId) => 
    dispatch(SEND_BULK_INVOICE(eventId, invoiceId)),
  refreshBulkInvoice: (eventId, invoiceId) => 
    dispatch(REFRESH_BULK_INVOICE(eventId, invoiceId)),
  createDelegation: (eventId, invoiceId) => 
    dispatch(CREATE_DELEGATION(eventId, invoiceId))
})

export default connect(mapStateToProps, mapDispatchToProps)(EventBulkInvoices)